/**
 * Flow: draw how listening should feel over time, and let Nova fill it from the
 * library. Curves and the song strip on the left, what it may choose from and
 * how it moves on the right, the answer as a list below.
 *
 * A flow keeps only settings, so it can be shared as a .novaflow file and
 * recreated from anyone's library; "Save as playlist" keeps one answer.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../core/app";
import { exportFlowFile, importFlowFile } from "../core/commands";
import { useDragReorder } from "../core/dragList";
import { player, usePlayer } from "../core/player";
import { FlowCanvas, targetNow } from "../flow/FlowCanvas";
import { FlowExport } from "../flow/FlowExport";
import { searchSeeds, type SeedOption } from "../flow/flowApi";
import { clock, METRIC_ORDER, METRICS, type FlowMode, type FlowPick, type Metric } from "../flow/flowMath";
import { takeFlowParams, useFlow, type FlowEditor } from "../flow/useFlowEditor";
import "../flow/flow.css";
import { QueryMatrix } from "../flow/QueryMatrix";
import { parse } from "../search/parse";
import { Artwork } from "../shell/Artwork";
import { Icon } from "../shell/Icons";
import { MOD } from "../shell/menus";
import { MoreButton } from "../shell/PopupMenu";
import { toast } from "../shell/Toast";
import { songs } from "../format/count";

const MODES: { id: FlowMode; label: string; hint: string }[] = [
  { id: "set", label: "Set length", hint: "Stop at the end of the curve" },
  { id: "repeat", label: "Repeat", hint: "Keep going; the curves start again" },
  { id: "endless", label: "Endless", hint: "Keep going; the curves hold their last values" },
];

function matchTone(m: number | null): string {
  if (m === null) return "none";
  return m >= 90 ? "hi" : m >= 80 ? "md" : "lo";
}

function why(p: FlowPick, spec: FlowEditor["spec"]): string {
  if (p.match === null) return "No curve to match";
  if (p.match >= 90) return "On the curve";
  const t = p.targets;
  if (t.bpm != null && p.bpm != null && Math.abs(p.bpm - t.bpm) > 10) return "Tempo far off — few songs fit here";
  if (t.energy != null && p.energyLevel != null && Math.abs(p.energyLevel - t.energy) >= 1)
    return p.energyLevel > t.energy ? "Energy a level high" : "Energy a level low";
  if (t.danceability != null && p.danceability != null && Math.abs(p.danceability - t.danceability) > 0.2) return "Danceability off";
  return spec.rules.strictness < 0.6 ? "Picked for variety" : "Close to the curve";
}

export function FlowView() {
  const f = useFlow();
  return f ? <FlowScreen f={f} /> : null;
}

function FlowScreen({ f }: { f: FlowEditor }) {
  const app = useApp();
  useEffect(() => f.want(), [f.want]);
  const [exporting, setExporting] = useState(false);
  const [managing, setManaging] = useState(false);
  const [tab, setTab] = useState<"settings" | "pool">("settings");
  const params = app.nav.params;

  // Opened from elsewhere: a saved flow by id, or a new one seeded with a song.
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    if (!takeFlowParams(paramsKey)) return;
    if (params.id) void f.openFlow(Number(params.id));
    else if (params.new) f.newFlow(params.seed ? { id: Number(params.seed), label: params.seedLabel ?? "" } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Ctrl+S saves the flow (the Settings window and other dialogs keep their own keys).
  const saveRef = useRef(f.save);
  saveRef.current = f.save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "s" || document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      if (!app.offline) void saveRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [app.offline]);

  const listRef = useRef<HTMLDivElement>(null);
  const drag = useDragReorder({ onMove: f.movePick, canDrag: f.movable });
  useEffect(() => {
    if (f.onAir >= 0) listRef.current?.querySelector(`[data-index="${f.onAir}"]`)?.scrollIntoView({ block: "nearest" });
  }, [f.onAir]);

  const flowMenu = () => [
    { id: "save", group: "a", label: "Save", shortcut: `${MOD}+S`, disabled: app.offline, run: () => void f.save() },
    { id: "save-new", group: "a", label: "Save as new flow…", disabled: app.offline, run: () => { const n = window.prompt("Name for the new flow", `${f.name} (copy)`); if (n) { f.setName(n); void f.save(true); } } },
    { id: "rename", group: "a", label: "Rename…", run: () => { const n = window.prompt("Rename flow", f.name); if (n) f.setName(n.trim() || f.name); } },
    { id: "playlist", group: "b", label: "Save answer as playlist…", icon: "list" as const, disabled: app.offline || !f.picks.length, run: () => setExporting(true) },
    { id: "export", group: "b", label: "Export flow file…", icon: "download" as const, hint: "A .novaflow holds the curves, rules and filters — no songs", disabled: app.offline,
      run: () => (f.flowId !== null && !f.dirty ? exportFlowFile({ id: f.flowId }) : exportFlowFile({ name: f.name, spec: f.engineSpec })) },
    { id: "import", group: "b", label: "Open flow file…", icon: "upload" as const, disabled: app.offline, run: () => importFlowFile(app) },
    { id: "saved", group: "c", label: "Saved flows…", icon: "list" as const, hint: "Open or delete any saved flow", disabled: app.offline || !f.flows.length, run: () => setManaging(true) },
    { id: "reset", group: "c", label: "Reset curves", icon: "refresh" as const, run: () => f.resetCurves() },
    { id: "delete", group: "c", label: f.flowId === null ? "Discard this flow" : "Delete flow", icon: "trash" as const, danger: true, run: () => void f.remove() },
  ];

  return (
    <section className="nv-view nv-flow" aria-label="Flow">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1 className="nv-editable-title">
            <span className="nv-autosize" data-value={f.name || "Untitled flow"}>
              <input value={f.name} onChange={(e) => f.setName(e.target.value)} aria-label="Flow name" size={1} placeholder="Untitled flow" />
            </span>
            <Icon name="edit" size={14} />
            {f.dirty && <span className="nv-dirty" title="Unsaved changes">•</span>}
          </h1>
          <div className="nv-sub">
            Flow · {Math.round(f.spec.length / 60)} min{f.spec.mode !== "set" ? ` · ${f.spec.mode}` : ""} · {songs(f.picks.length)}
            {f.averageMatch !== null && ` · average match ${f.averageMatch}%`}
            {f.pool !== null && ` · from ${songs(f.pool)}`}
            {f.curating && " · choosing…"}
            {f.handPlaced > 0 && ` · ${f.handPlaced} placed by you`}
          </div>
        </div>
        <div className="nv-actions">
          {f.live ? (
            <button className="nv-btn primary" onClick={() => player.toggle()} title="The flow is playing in the player below">
              <Icon name={f.playing ? "pause" : "play"} /> {f.playing ? "Pause" : "Resume"}
            </button>
          ) : (
            <button className="nv-btn primary" disabled={!f.picks.length} onClick={() => f.play(0)} title="Plays in the player below">
              <Icon name="play" /> Play flow
            </button>
          )}
          <button className="nv-btn" disabled={app.offline} title="A different answer to the same curves" onClick={f.newAnswer}>
            <Icon name="refresh" /> New answer
          </button>
          <button className="nv-btn" disabled={app.offline} onClick={() => void f.save()}>Save</button>
          <select className="nv-select" value={f.flowId ?? ""} aria-label="Open a saved flow" onChange={(e) => (e.target.value ? void f.openFlow(Number(e.target.value)) : f.newFlow())}>
            <option value="">{f.flowId === null ? "Unsaved flow" : "New flow"}</option>
            {f.flows.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <MoreButton label="Flow options" items={flowMenu} />
        </div>
      </div>

      <div className="nv-flow-bar">
        <div className="nv-seg" role="radiogroup" aria-label="When the curve ends">
          {MODES.map((m) => (
            <button key={m.id} role="radio" aria-checked={f.spec.mode === m.id} className={f.spec.mode === m.id ? "on" : ""} title={m.hint} onClick={() => f.setMode(m.id)}>
              {m.id === "set" ? `${Math.round(f.spec.length / 60)} min` : m.label}
            </button>
          ))}
        </div>
        <label className="nv-inline" title="Length of the curve, in minutes">
          <input type="number" min={5} max={240} value={Math.round(f.spec.length / 60)} onChange={(e) => f.setLength(Number(e.target.value) || 60)} aria-label="Length in minutes" /> min
        </label>
        <span className="nv-vsep" />
        <span className="nv-dim">Curves</span>
        {METRIC_ORDER.map((m) => {
          const on = f.spec.metrics.includes(m);
          return (
            <span key={m} className={`nv-mchip ${f.active === m && on ? "edit" : ""} ${on ? "" : "off"}`} style={{ ["--metric" as string]: METRICS[m].color }}>
              <input type="checkbox" checked={on} onChange={() => f.toggleMetric(m)} aria-label={`Steer by ${METRICS[m].label}`} />
              <button onClick={() => (on ? f.setActive(m) : f.toggleMetric(m))} title={on ? "Edit this curve" : "Start steering by this"}>
                <i />
                {METRICS[m].label}
              </button>
            </span>
          );
        })}
        <span className="nv-spacer" />
        <span className="nv-hint">Click to add a point · drag to move · double-click to remove</span>
      </div>

      <div className="nv-flow-main">
        <div className="nv-flow-left">
          <div className="nv-card-box nv-flow-chart">
            <FlowCanvas spec={f.spec} active={f.active} picks={f.picks} position={f.now} onCurve={f.setCurve} onSeek={(t) => f.play(t)} />
            <SongStrip f={f} />
            <div className="nv-legend">
              <span><b style={{ borderColor: "var(--curve-energy)" }} />Energy target</span>
              <span><b className="thick" style={{ borderColor: METRICS[f.active].color }} />Chosen songs</span>
              <span><b style={{ borderColor: "var(--curve-bpm)" }} />BPM ±5</span>
              <span><b className="dashed" style={{ borderColor: "var(--curve-dance)" }} />Danceability</span>
              <span className="nv-spacer" />
              <span>Bar under each song: <i className="dot hi" /> on curve <i className="dot md" /> close <i className="dot lo" /> off</span>
            </div>
          </div>
          {f.notice?.bad && <div className="nv-warn bad">{f.notice.text}</div>}
          {f.warnings.map((w) => <div key={w} className="nv-warn">{w}</div>)}
          <div className="nv-gridwrap nv-flow-list" ref={listRef}>
            <table className="nv-table">
              <thead>
                <tr>
                  <th className="nv-grip-col" aria-label="Drag" /><th className="num">Starts</th><th>Title</th><th>Artist</th><th className="num">BPM → target</th><th>Energy</th><th>Match</th><th>Why</th><th />
                </tr>
              </thead>
              <tbody>
                {f.picks.map((p, i) => {
                  const row = drag.rowProps(i);
                  return (
                    <tr
                      key={`${p.trackId}@${i}`}
                      data-index={i}
                      {...row}
                      className={`${i === f.onAir ? "playing" : ""} ${p.pinned ? "pinned" : ""} ${row.className}`}
                      onDoubleClick={() => f.play(p.start)}
                      title="Double-click to play from here · drag to move"
                    >
                      <td className="nv-grip-col">{f.movable(i) && <Icon name={p.pinned ? "pin" : "grip"} size={14} className={`nv-grip ${p.pinned ? "on" : ""}`} />}</td>
                      <td className="num tnum">{clock(p.start)}</td>
                      <td><span className="title-cell"><Artwork trackId={p.trackId} seed={p.artist ?? p.title ?? ""} size={24} rounded={4} /><span className="title">{p.title ?? "Untitled"}</span></span></td>
                      <td className="dim">{p.artist}</td>
                      <td className="num tnum">
                        {p.bpm !== null ? Math.round(p.bpm) : "—"}
                        {p.targets.bpm != null && <span className="nv-dim"> → {Math.round(p.targets.bpm)}</span>}
                      </td>
                      <td>{p.energyLevel != null ? <span className="energy-bars">{[1, 2, 3, 4, 5].map((n) => <i key={n} className={n <= (p.energyLevel ?? 0) ? "f" : ""} />)}</span> : "—"}</td>
                      <td><span className={`nv-match ${matchTone(p.match)}`}>{p.match !== null ? `${Math.round(p.match)}%` : "—"}</span></td>
                      <td className="dim">{i === f.onAir ? "Playing" : p.pinned ? "Placed by you" : why(p, f.spec)}</td>
                      <td className="col-more">
                        <MoreButton label={`Options for ${p.title ?? "this song"}`} items={[
                          { id: "play", label: "Play from here", icon: "play", run: () => f.play(p.start) },
                          { id: "up", group: "b", label: "Move up", disabled: !f.movable(i - 1) || i === 0, run: () => f.movePick(i, i - 1) },
                          { id: "down", group: "b", label: "Move down", disabled: !f.movable(i) || i >= f.picks.length - 1, run: () => f.movePick(i, i + 1) },
                          { id: "pin", group: "b", label: p.pinned ? "Let Nova choose this place" : "Keep this song here", checked: !!p.pinned, disabled: !f.movable(i), run: () => f.setPinned(i, !p.pinned) },
                          { id: "remove", group: "c", label: "Take it out of this flow", icon: "close", danger: true, disabled: !f.movable(i), run: () => f.removePick(i) },
                        ]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!f.picks.length && <div className="nv-empty">{f.curating ? "Choosing songs…" : app.offline ? "Start the engine to fill this flow from your library." : "No songs fit yet. Draw a curve, or loosen the filters on the right."}</div>}
          </div>
        </div>

        <aside className="nv-insp nv-flow-side" aria-label="Flow settings">
          <div className="nv-tabs" role="tablist">
            <button role="tab" aria-selected={tab === "settings"} className={tab === "settings" ? "on" : ""} onClick={() => setTab("settings")}>Settings</button>
            <button role="tab" aria-selected={tab === "pool"} className={tab === "pool" ? "on" : ""} onClick={() => setTab("pool")}>Now</button>
          </div>
          <div className="nv-insp-scroll">
            {tab === "settings" ? <FlowSettings f={f} /> : <FlowNow f={f} />}
          </div>
          <div className="nv-insp-foot">
            <button className="nv-btn grow" disabled={app.offline || !f.picks.length} onClick={() => setExporting(true)}>
              <Icon name="list" /> Save as playlist
            </button>
            <MoreButton label="Flow options" items={flowMenu} />
          </div>
        </aside>
      </div>

      {managing && <SavedFlows f={f} onClose={() => setManaging(false)} />}
      {exporting && (
        <FlowExport
          picks={f.exportable}
          defaultName={f.name}
          onClose={() => setExporting(false)}
          onDone={async (playlistId) => {
            setExporting(false);
            await app.library.refreshPlaylists();
            toast("Saved as a playlist.", "ok");
            app.navigate("playlist", { id: String(playlistId) });
          }}
        />
      )}
    </section>
  );
}

/** Every saved flow, to open or delete without opening it first. */
function SavedFlows({ f, onClose }: { f: FlowEditor; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="nv-modal" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nv-dialog nv-box" role="dialog" aria-modal="true" aria-label="Saved flows">
        <header>
          <h2>Saved flows</h2>
          <button className="nv-iconbtn" title="Close (Esc)" onClick={onClose}><Icon name="close" /></button>
        </header>
        <div className="nv-box-body">
          {!f.flows.length && <p className="nv-dim">No saved flows.</p>}
          <ul className="fl-saved">
            {f.flows.map((x) => (
              <li key={x.id} className={x.id === f.flowId ? "on" : ""}>
                <button className="fl-saved-name" onClick={() => { void f.openFlow(x.id); onClose(); }} title="Open this flow">
                  {x.name}{x.id === f.flowId && <span className="nv-dim"> · open now</span>}
                </button>
                <button className="nv-iconbtn" title={`Delete “${x.name}”`} aria-label={`Delete “${x.name}”`}
                  onClick={async () => { if (await f.removeSaved(x.id, x.name)) toast(`Deleted “${x.name}”.`, "ok"); }}>
                  <Icon name="trash" size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <footer className="nv-box-foot"><span className="nv-spacer" /><button className="nv-btn" onClick={onClose}>Done</button></footer>
      </div>
    </div>
  );
}

function SongStrip({ f }: { f: FlowEditor }) {
  const total = Math.max(f.spec.length, 1);
  const shown = f.picks.filter((p) => f.spec.mode !== "set" || p.start < total);
  return (
    <div className="nv-lane" role="list" aria-label="Songs along the flow">
      {shown.map((p, i) => {
        const start = Math.min(p.start, total);
        const width = (Math.max(0, Math.min(total, p.start + p.air) - start) / total) * 100;
        if (width <= 0) return null;
        return (
          <button
            key={`${p.trackId}@${p.start}`}
            role="listitem"
            className={`nv-lane-song ${i === f.onAir ? "on" : ""}`}
            style={{ left: `${(start / total) * 100}%`, width: `${width}%` }}
            title={`${clock(p.start)} · ${p.title ?? "Untitled"} — ${p.artist ?? ""}${p.match !== null ? ` · match ${Math.round(p.match)}%` : ""}\nClick to play from here`}
            onClick={() => f.play(p.start)}
          >
            <Artwork trackId={p.trackId} seed={p.artist ?? p.title ?? ""} className="bg" rounded={5} />
            <span className="nv-lane-text">
              <b>{p.title ?? "Untitled"}</b>
              <small>{p.bpm != null ? Math.round(p.bpm) : "—"} · E{p.energyLevel ?? "—"}</small>
            </span>
            <i className={`nv-lane-match ${matchTone(p.match)}`} />
          </button>
        );
      })}
    </div>
  );
}

function Slider({ label, hint, value, min, max, step, format, onChange }: {
  label: string; hint: string; value: number; min: number; max: number; step: number;
  format: (v: number) => string; onChange: (v: number) => void;
}) {
  return (
    <label className="nv-slider" title={hint}>
      <span className="nv-slider-head"><span>{label}</span><b>{format(value)}</b></span>
      <input type="range" min={min} max={max} step={step} value={value} style={{ ["--fill" as string]: `${((value - min) / (max - min)) * 100}%` }} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

function FlowSettings({ f }: { f: FlowEditor }) {
  const app = useApp();
  const r = f.spec.rules;
  const p = usePlayer();
  const genres = app.library.genres;
  const columns = useMemo(() => app.library.columns.map((c) => ({ key: c.key, label: c.label, type: c.type })), [app.library.columns]);
  return (
    <>
      <FlowSections f={f} />
      <section className="nv-panel-sec">
        <h5>Songs to choose from {f.pool !== null && <span className="nv-count">{f.pool.toLocaleString()} fit</span>}</h5>
        <QueryMatrix
          input={f.input}
          setInput={f.setInput}
          typedQuery={f.typedQuery}
          filters={f.filters}
          setFilters={f.setFilters}
          offline={app.offline}
          genres={genres}
          columns={columns}
          count={f.pool}
          loading={f.curating}
        />
        <label className="nv-panel-check" title="Jingles, station IDs and anything shorter than 30 seconds stay out of this flow">
          <span>Songs only <em className="nv-dim">no jingles or clips under 30 s</em></span>
          <span className="nv-toggle">
            <input type="checkbox" role="switch" checked={r.songsOnly !== false} onChange={(e) => f.setRule("songsOnly", e.target.checked)} />
            <i aria-hidden="true" />
          </span>
        </label>
        <label className="nv-panel-check" title="Off: the flow plays as built. On: the clip rules (a station ID every 4 songs, say) play between its songs, wherever it plays.">
          <span>Clip rules <em className="nv-dim">play the clip rules between its songs</em></span>
          <span className="nv-toggle">
            <input type="checkbox" role="switch" checked={r.clipRules === true} onChange={(e) => f.setRule("clipRules", e.target.checked)} />
            <i aria-hidden="true" />
          </span>
        </label>
      </section>
      <section className="nv-panel-sec">
        <h5>Opens with</h5>
        <SeedPicker f={f} />
      </section>
      <section className="nv-panel-sec">
        <h5>How it moves</h5>
        <Slider label="Follow the curve" hint="100% always takes the closest song; lower mixes in variety" value={r.strictness} min={0} max={1} step={0.05} format={(v) => `${Math.round(v * 100)}%`} onChange={(v) => f.setRule("strictness", v)} />
        <Slider label="Tempo ramp, per song" hint="Most the BPM may change from one song to the next" value={r.maxBpmStep} min={1} max={30} step={1} format={(v) => `±${v} BPM`} onChange={(v) => f.setRule("maxBpmStep", v)} />
        <Slider label="Crossfade" hint="How long one song plays under the next. The same setting as Playback → Crossfade." value={p.crossfade} min={0} max={12} step={1} format={(v) => (v ? `${v} s` : "none")} onChange={(v) => player.setCrossfade(v)} />
        <Slider label="Same artist again after" hint="Minutes before an artist (any credited artist) can return" value={r.artistMinutes} min={0} max={180} step={5} format={(v) => `${v} min`} onChange={(v) => f.setRule("artistMinutes", v)} />
        <Slider label="Same song again after" hint="Minutes before the same song can return" value={r.titleMinutes} min={0} max={720} step={15} format={(v) => (v >= 60 ? `${Math.round((v / 60) * 10) / 10} h` : `${v} min`)} onChange={(v) => f.setRule("titleMinutes", v)} />
      </section>
    </>
  );
}

/**
 * Sections: the flow's time split by its maker (20 minutes, say), in order, as
 * many as fit. Each is its own curve -- the points drawn inside its part of the
 * graph. Time they leave holds the last section's pace (on the radio, Auto DJ's).
 */
function FlowSections({ f }: { f: FlowEditor }) {
  const sections = f.spec.sections ?? [];
  const filled = sections.reduce((n, s) => n + s.length, 0);
  const left = f.spec.length - filled;
  const minutes = (sec: number) => Math.round(sec / 60);
  let at = 0;
  return (
    <section className="nv-panel-sec" aria-label="Sections">
      <h5>Sections {sections.length > 0 && <span className="nv-count">{sections.length}</span>}</h5>
      {!sections.length && (
        <p className="nv-dim">One curve for the whole flow. Split it into sections, each with its own curve, drawn in its part of the graph.</p>
      )}
      {sections.length > 0 && (
        <ol className="fl-sections">
          {sections.map((s, i) => {
            const start = at;
            at += s.length;
            return (
              <li key={i}>
                <span className="fl-sec-at" title="Starts at">{clock(start)}</span>
                <input className="nv-input fl-sec-name" value={s.name} maxLength={60} aria-label={`Section ${i + 1} name`}
                  onChange={(e) => f.setSection(i, { name: e.target.value })} />
                <input className="nv-input fl-sec-min" type="number" min={1} max={minutes(s.length + left)} value={minutes(s.length)}
                  aria-label={`${s.name} length in minutes`} title="Minutes"
                  onChange={(e) => e.target.value !== "" && f.setSection(i, { minutes: Number(e.target.value) })} />
                <button className="nv-iconbtn sm" title={`Remove “${s.name}”`} aria-label={`Remove section ${s.name}`} onClick={() => f.removeSection(i)}>
                  <Icon name="close" size={13} />
                </button>
              </li>
            );
          })}
        </ol>
      )}
      <div className="fl-sec-foot">
        <button className="nv-btn sm" disabled={left < 60} onClick={f.addSection}
          title={left < 60 ? "The sections fill the flow: lengthen it, or shorten a section" : "Add a section after the last"}>
          <Icon name="plus" size={13} /> Add a section
        </button>
        {sections.length > 0 && (
          <span className="nv-dim">
            {minutes(filled)} of {minutes(f.spec.length)} min{left >= 60 ? ` · the last ${minutes(left)} min holds the last section's pace` : ""}
          </span>
        )}
      </div>
    </section>
  );
}

function SeedPicker({ f }: { f: FlowEditor }) {
  const app = useApp();
  const [text, setText] = useState("");
  const [options, setOptions] = useState<SeedOption[]>([]);
  useEffect(() => {
    if (!text.trim() || app.offline) return setOptions([]);
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const ast = parse(text);
      searchSeeds({ clauses: ast.clauses, free: ast.free }, controller.signal).then(setOptions).catch(() => undefined);
    }, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [text, app.offline]);

  if (f.spec.seedTrackId) {
    return (
      <div className="nv-seed">
        <Artwork trackId={String(f.spec.seedTrackId)} seed={f.spec.seedLabel ?? ""} size={34} rounded={5} />
        <div className="nv-seed-text"><b>{(f.spec.seedLabel ?? `Song ${f.spec.seedTrackId}`).split(" — ")[0]}</b><span>{(f.spec.seedLabel ?? "").split(" — ")[1] ?? ""}</span></div>
        <MoreButton label="Opening song options" items={[{ id: "clear", label: "No opening song", icon: "close", run: () => f.setSeed(null, null) }]} />
      </div>
    );
  }
  return (
    <div className="nv-seed-search">
      <input className="nv-input" placeholder="Optional: search for a song to open with" value={text} disabled={app.offline} onChange={(e) => setText(e.target.value)} aria-label="Opening song" />
      {options.length > 0 && (
        <div className="nv-seed-options" role="listbox">
          {options.map((o) => (
            <button key={o.id} role="option" aria-selected={false} onClick={() => { f.setSeed(Number(o.id), `${o.title} — ${o.artist}`); setText(""); }}>
              <b>{o.title}</b> <span className="nv-dim">{o.artist}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FlowNow({ f }: { f: FlowEditor }) {
  const pick = f.onAir >= 0 ? f.picks[f.onAir] : null;
  if (!pick) return <div className="nv-empty">Press Play flow to hear it. While it plays, change a curve and the rest re-chooses itself from the next song.</div>;
  return (
    <>
      <div className="nv-cover"><Artwork trackId={pick.trackId} seed={pick.artist ?? ""} rounded={10} /></div>
      <div className="nv-insp-title"><h2>{pick.title}</h2><div className="nv-sub">{pick.artist}</div></div>
      <dl className="nv-kv">
        {f.spec.metrics.map((m: Metric) => {
          const target = targetNow(f.spec, m, f.now ?? 0);
          const actual = m === "energy" ? pick.energyLevel : m === "bpm" ? pick.bpm : pick.danceability;
          return (
            <div key={m} style={{ display: "contents" }}>
              <dt style={{ color: METRICS[m].color }}>{METRICS[m].label}</dt>
              <dd>{actual != null ? METRICS[m].format(actual) : "—"}{target !== null && <span className="nv-dim"> · target {METRICS[m].format(target)}</span>}</dd>
            </div>
          );
        })}
        <dt>Match</dt><dd>{pick.match !== null ? `${Math.round(pick.match)}%` : "—"}</dd>
        <dt>Time</dt><dd>{clock(f.now ?? 0)} of {clock(f.plan.total)}</dd>
      </dl>
    </>
  );
}
